import { type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Floating popover (design system) — renders its children in a portal on
 * `document.body` with `position: fixed`, anchored to a trigger element. This
 * escapes any `overflow:hidden|auto` ancestor (e.g. a Modal body), so dropdowns
 * and date pickers are never clipped. Flips above the anchor when there isn't
 * room below. Calls `onClose` on outside mousedown (anchor + popover excluded).
 */
type Props = {
  anchorRef: RefObject<HTMLElement | null>
  open: boolean
  onClose: () => void
  children: ReactNode
  className?: string
  /** Match the anchor's width (dropdowns). Otherwise the popover sizes to content. */
  matchWidth?: boolean
  /** Extra px added to the matched width, so a dropdown can be a touch wider than
   * its trigger (e.g. comboboxes with long labels). Ignored without `matchWidth`. */
  extraWidth?: number
}

export default function Popover({
  anchorRef,
  open,
  onClose,
  children,
  className = '',
  matchWidth,
  extraWidth = 0,
}: Props) {
  const popRef = useRef<HTMLDivElement>(null)
  const [style, setStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    top: 0,
    left: 0,
    visibility: 'hidden',
  })

  useLayoutEffect(() => {
    if (!open) return
    function place() {
      const anchor = anchorRef.current
      if (!anchor) return
      const r = anchor.getBoundingClientRect()
      const gap = 4
      const popH = popRef.current?.offsetHeight ?? 0
      const popW = popRef.current?.offsetWidth ?? 0
      const below = window.innerHeight - r.bottom
      const flip = popH > 0 && popH > below && r.top > below
      // Keep the popover within the viewport horizontally: a trigger near the
      // right edge (e.g. the top-bar avatar menu) would otherwise overflow.
      const left = popW > 0 ? Math.max(8, Math.min(r.left, window.innerWidth - popW - 8)) : r.left
      setStyle({
        position: 'fixed',
        top: flip ? Math.max(8, r.top - gap - popH) : r.bottom + gap,
        left,
        // Pin the dropdown to the trigger width (plus any extra) so long option
        // labels can't widen it past the field — they truncate instead.
        ...(matchWidth ? { width: r.width + extraWidth } : {}),
        visibility: 'visible',
        zIndex: 100,
      })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, anchorRef, matchWidth, extraWidth])

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      const t = e.target as Node
      if (anchorRef.current?.contains(t) || popRef.current?.contains(t)) return
      onClose()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, onClose, anchorRef])

  if (!open) return null

  return createPortal(
    <div ref={popRef} style={style} className={className}>
      {children}
    </div>,
    document.body,
  )
}
